import { useState, useEffect, useCallback } from "react";
import { localStorageManager } from "@/services/localStorage";
import { LOCAL_STORAGE_BASE_KEYS } from "@/types/v2";

export interface UseShowAllRepairOrdersProps {
  shopId?: string;
  defaultValue?: boolean;
}

export interface UseShowAllRepairOrdersResult {
  showAllRepairOrders: boolean;
  setShowAllRepairOrders: (show: boolean) => void;
  toggleShowAllRepairOrders: () => void;
  isToggleAvailable: boolean;
}

export const useShowAllRepairOrders = ({
  shopId,
  defaultValue = false,
}: UseShowAllRepairOrdersProps): UseShowAllRepairOrdersResult => {
  // Create storage key specific to shop
  const storageKey = LOCAL_STORAGE_BASE_KEYS.SHOW_ALL_REPAIR_ORDERS;

  // Initialize state with localStorage or default
  const [showAllRepairOrders, setShowAllRepairOrdersState] = useState<boolean>(
    () => {
      try {
        const stored = localStorageManager.getItem(storageKey, shopId);
        if (stored) {
          return JSON.parse(stored) as boolean;
        }
      } catch (error) {
        console.warn(
          "Failed to load show all repair orders setting from localStorage:",
          error
        );
      }

      return defaultValue;
    }
  );

  // Save to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorageManager.setItem(
        storageKey,
        JSON.stringify(showAllRepairOrders),
        shopId
      );
    } catch (error) {
      console.warn(
        "Failed to save show all repair orders setting to localStorage:",
        error
      );
    }
  }, [showAllRepairOrders, storageKey, shopId]);

  // Update toggle state
  const setShowAllRepairOrders = useCallback((show: boolean) => {
    setShowAllRepairOrdersState(show);
  }, []);

  // Toggle current state
  const toggleShowAllRepairOrders = useCallback(() => {
    setShowAllRepairOrdersState((prev) => !prev);
  }, []);

  // Toggle is available when it makes sense (could be used for future enhancements)
  const isToggleAvailable = true;

  return {
    showAllRepairOrders,
    setShowAllRepairOrders,
    toggleShowAllRepairOrders,
    isToggleAvailable,
  };
};
